import { action, observable } from 'mobx';
import {
  MapView,
  GroupLayer,
  Polygon,
  SpatialReference,
  Point,
  Graphic,
  Polyline,
  SimpleFillSymbol,
  SimpleLineSymbol,
  SimpleMarkerSymbol,
  Geometry,
  esriProjection,
} from 'cd-eft/utils/esriTypes';
import { MeasureResultsModel } from 'cd-eft/components/common/MeasureTools/MeasureResultsModel';
import { MeasureInputType } from 'cd-eft/business/MapViewInputManager/MeasureInputResult';
import { MapViewInputManager } from 'cd-eft/business/MapViewInputManager/MapViewInputManager';
import { DrawingsModel } from 'cd-eft/components/common/DrawingTools/model/DrawingsModel';
import { DrawingToolsetViewModel } from 'cd-eft/components/common/DrawingTools/viewModel/DrawingToolsetViewModel';
import { AddBreaklineTool } from 'cd-eft/components/Design/BreaklineTools/viewModel/AddBreaklineTool';
import { DeleteBreaklineTool } from 'cd-eft/components/Design/BreaklineTools/viewModel/DeleteBreaklineTool';
import { ToolSlot } from 'cd-eft/components/Design/BreaklineTools/framework/Tool';
import { DeleteBorderPointTool } from 'cd-eft/components/Design/BreaklineTools/viewModel/DeleteBorderPointTool';
import { InsertBorderPointsTool } from 'cd-eft/components/Design/BreaklineTools/viewModel/InsertBorderPointsTool';
import { MeasureDistanceUnits, MeasureAreaUnits } from 'cd-eft/components/common/MeasureTools/MeasureToolboxView';
import { WaterwayVertexParamDTO } from 'cd-eft/_codegen_/openapi-generator/eft-set';
import { EditTinablePointsTool } from 'cd-eft/components/Design/BreaklineTools/viewModel/EditTinablePointsTool';
import { MoveBreaklineTool } from 'cd-eft/components/Design/BreaklineTools/viewModel/MoveBreaklineTool';
import { AddPointBreaklineTool } from 'cd-eft/components/Design/BreaklineTools/viewModel/AddPointBreaklineTool';
import { DeletePointBreaklineTool } from 'cd-eft/components/Design/BreaklineTools/viewModel/DeletePointBreaklineTool';
import { ExtendBreaklineTool } from 'cd-eft/components/Design/BreaklineTools/viewModel/ExtendBreaklineTool';
import stores from 'cd-eft/models/Stores';
import TextSymbol from '@arcgis/core/symbols/TextSymbol';

const { projectsLibraryStore } = stores.AppStore;

export interface IPendingEditManagerVMConstructorProps {
  mapView: MapView;
  toolSlot: ToolSlot;
  component: 'Design' | 'Analysis';
}

interface IWDTGroup {
  groupID: number;
  waterWayVertextParamDTOMap: Map<string, WaterwayVertexParamDTO>;
}

export abstract class PendingEditManagerVM {
  public readonly mapView: MapView;

  @observable activeTab: string = '';
  @observable hideToolbar: boolean = true;
  @observable measureDistanceUnits: MeasureDistanceUnits = 'feet';
  @observable measureAreaUnits: MeasureAreaUnits = 'acres';
  @observable measurePerimeterUnits: MeasureDistanceUnits = 'feet';
  @observable measureToolType: MeasureInputType | undefined;
  @observable uncommittedEdits: number = 0;
  @observable uncommittedDrawings: number = 0;
  @observable wdtGroups: IWDTGroup[] = [];
  @observable wdtGroupsLength: number = 0;
  @observable disableUndo: boolean = true;
  @observable terraceGroupsLength: number = 0;
  @observable ugoGroupsLength: number = 0;
  @observable breakLineGroupsLength: number = 0;
  @observable uniqueDescriptions: string[] = [];
  @observable selctedPointDescription: string = '';
  @observable isClosed: boolean = false;
  @observable component: 'Design' | 'Analysis';

  // Surface editing tools
  @observable addBreaklineTool?: AddBreaklineTool;
  @observable deleteBreaklineTool?: DeleteBreaklineTool;
  @observable addBorderTool?: InsertBorderPointsTool;
  @observable deleteBorderTool?: DeleteBorderPointTool;
  @observable editTinableTool?: EditTinablePointsTool;
  @observable moveBreaklineTool?: MoveBreaklineTool;
  @observable addPointBreaklineTool?: AddPointBreaklineTool;
  @observable deletePointBreaklineTool?: DeletePointBreaklineTool;
  @observable extendBreaklineTool?: ExtendBreaklineTool;

  readonly measuresGroupLayer: GroupLayer = new GroupLayer({
    id: 'measures',
    title: 'Measures',
  });
 
  public drawingsGroupLayerByDesignElementType: any = {
    0: new GroupLayer({
      id: 'GISBoxVM.Drawings',
      title: 'Drawings',
    }),
    2: new GroupLayer({
      id: 'SurfaceDesignerVm.Drawings',
      title: 'Surface drawings',
    }),
    4: new GroupLayer({
      id: 'terrace.Drawings',
      title: 'Terrace drawings',
    }),
    5: new GroupLayer({
      id: 'alignment.Drawings',
      title: 'Alignment drawings',
    }),
    7: new GroupLayer({
      id: 'waterway.Drawings',
      title: 'Waterway drawings',
    }),
    11: new GroupLayer({
      id: 'pitRectangular.Drawings',
      title: 'Pit drawings',
    }),
    12: new GroupLayer({
      id: 'pitRound.Drawings',
      title: 'Pit drawings',
    }),
    13: new GroupLayer({
      id: 'pitDiked.Drawings',
      title: 'Pit drawings',
    }),
    14: new GroupLayer({
      id: 'pad.Drawings',
      title: 'Pad drawings',
    }),
    15: new GroupLayer({
      id: 'landleveling.Drawings',
      title: 'Land leveling drawings',
    }),
    25: new GroupLayer({
      id: 'ugo.Drawings',
      title: 'UGO drawings', //Underground_Outlet drawings
    }),
  };
  public drawingsByDesignElementType = {};
  drawingsGroupLayer: GroupLayer = this.drawingsGroupLayerByDesignElementType[2];
  public drawings: DrawingsModel = new DrawingsModel(this.drawingsGroupLayer);
  public readonly inputManager: MapViewInputManager;
  public drawingTools: DrawingToolsetViewModel;
  public readonly toolSlot: ToolSlot;

  public measureResults = new MeasureResultsModel(this.measuresGroupLayer);

  abstract onSavePendingEdits(e);
  abstract onSaveStructurePendingEdits(e);
  abstract setUncommittedEdits();
  abstract onUndoLastEdit();
  abstract onClearAllEdits();
  abstract setUniqueDescriptions();
  abstract addBreaklineEdits();
  abstract toggleToolbar(designElementTypeId: number, surfaceType);
  abstract onClickBreaklinesTool(e);
  abstract selectDescription(value);
  abstract toggleBreaklineClosed(event: React.ChangeEvent<HTMLInputElement>);
  abstract onClickBorderpointsTool(e);
  abstract onClickTinableTool(e);
  abstract onClickGenerate();
  abstract openToolBar();

  constructor(props: IPendingEditManagerVMConstructorProps) {
    this.component = props.component;

    // Set up drawings
    if (props.component === 'Analysis') {
      this.drawingsGroupLayer = this.drawingsGroupLayerByDesignElementType[0];
      this.drawings = new DrawingsModel(this.drawingsGroupLayer);
      this.drawingsByDesignElementType = new DrawingsModel(this.drawingsGroupLayer);
    } else if (props.component === 'Design') {
      for (const key of Object.keys(this.drawingsGroupLayerByDesignElementType)) {
        if (key !== '0') this.drawingsByDesignElementType[key] = new DrawingsModel(this.drawingsGroupLayerByDesignElementType[key]);
      }
    }
    this.mapView = props.mapView;
    this.toolSlot = props.toolSlot;
    this.inputManager = new MapViewInputManager(this.mapView);
    this.drawingTools = new DrawingToolsetViewModel(this.inputManager, this.drawings);
  }

  @action.bound async closeToolBar(designElementTypeId: number, surfaceType) {
    this.hideToolbar = true;

    // cancel any current input operation
    await this.inputManager.cancelCurrentOperationAsync();
    if (this.toolSlot.current) {
      await this.toolSlot.current?.deactivate();
    }
    // reset to default tab
    if (designElementTypeId !== 2 || surfaceType === 'dem') {
      this.activeTab = 'Measure';
      this.measureToolType = undefined;
      this.measureResults = new MeasureResultsModel(this.measuresGroupLayer);
    } else {
      this.activeTab = 'Breaklines';
    }
  }

  @action.bound async onClickMeasureTool(e: any) {
    e.preventDefault();
    await this.inputManager.cancelCurrentOperationAsync();
    if (this.toolSlot.current) {
      await this.toolSlot.current?.deactivate();
    }
    this.activeTab = 'Measure';
  }

  @action.bound async onClickDrawingTool(e: any) {
    e.preventDefault();
    await this.inputManager.cancelCurrentOperationAsync();
    if (this.toolSlot.current) {
      await this.toolSlot.current?.deactivate();
    }
    this.activeTab = 'Drawing';
  }

  @action.bound onChangeMeasureTool(toolType: MeasureInputType) {
    this.measureToolType = toolType;
  }

  @action.bound async onChangeDistanceUnits(e: any) {
    if (e.target.value !== undefined && e.target.value !== '') {
      this.measureDistanceUnits = e.target.value;
    } else {
      this.measureDistanceUnits = 'feet';
    }
    await this.inputManager.cancelCurrentOperationAsync();
  }

  @action.bound async onChangeAreaPerimeterUnits(e: any) {
    if (e.target.value !== undefined && e.target.value !== '') {
      [this.measureAreaUnits, this.measurePerimeterUnits] = (e.target.value as string).split('/') as any[];
    } else {
      this.measureAreaUnits = 'acres';
      this.measurePerimeterUnits = 'feet';
    }
    await this.inputManager.cancelCurrentOperationAsync();
  }

  @action.bound userLeavingUncommitedEditsWarningOnOk() {
    return false;
  }

  protected generatePolygon(xyzarray: Point[], color: string, width: number) {
    const spatialReference = projectsLibraryStore.projectSpatialReference;

    const polygon = new Polygon({
      spatialReference: spatialReference,
      hasZ: true,
    });

    const ring = this.generatePointGeometryList(xyzarray, spatialReference);
    polygon.addRing(ring);

    const graphic = new Graphic({
      geometry: polygon,
      symbol: {
        type: 'simple-fill',
        outline: {
          color,
          width: width,
        },
      } as any,
    });

    return graphic;
  }

  public generatePointGeometryList(xyzarray, spatialReference?: SpatialReference): Point[] {
    if (!spatialReference) {
      spatialReference = projectsLibraryStore.projectSpatialReference!;
    }

    const pointList: Point[] = [];

    for (const element of xyzarray) {
      const point = new Point({
        spatialReference: spatialReference,
        hasZ: true,
        x: element.x,
        y: element.y,
        z: element.z,
      });
      pointList.push(point);
    }
    return pointList;
  }

  protected generatePolyline(paths: Point[], color: string, width: number): any {
    const spatialReference = projectsLibraryStore.projectSpatialReference;

    const polyline = new Polyline({
      spatialReference: spatialReference,
      hasZ: true,
    });

    paths.forEach((path, i) => {
      if (i > 0) {
        const point1 = new Point({
          spatialReference: spatialReference,
          hasZ: true,
          x: paths[i - 1].x,
          y: paths[i - 1].y,
          z: paths[i - 1].z,
        });

        const point2 = new Point({
          spatialReference: spatialReference,
          hasZ: true,
          x: path.x,
          y: path.y,
          z: path.z,
        });

        polyline.addPath([point1, point2]);
      }
    });

    const graphic = new Graphic({
      geometry: polyline,
      symbol: {
        type: 'simple-line',
        outline: {
          color,
          width,
        },
      } as any,
    });

    return graphic;
  }

  protected extractDrawingJson() {
    const drawingsJson: any[] = [];
    const drawings = this.drawings.drawingSyncSet;
    let dataSpatialReference = projectsLibraryStore.projectSpatialReference!;
    if (this.component === 'Analysis' && !(dataSpatialReference.wkid || dataSpatialReference.wkt)) {
      dataSpatialReference = new SpatialReference({ wkid: 3857 });
    }

    for (const graphic of drawings.getItems()) {
      const geometry = esriProjection.project(graphic.geometry, dataSpatialReference) as Geometry;
      switch (geometry.type) {
        case 'point':
          const point = geometry as Point;

          if (graphic.symbol.type === 'text') {
            const symbol = graphic.symbol as TextSymbol;
            drawingsJson.push({
              drawingType: 'Text',
              featureName: 'My Text',
              featureDescription: symbol.text,
              color: symbol.color.toHex(),
              fontName: symbol.font.family,
              fontStyle: symbol.font.style,
              fontSize: symbol.font.size,
              xyzarray: [
                {
                  x: point.x,
                  y: point.y,
                  z: 0,
                },
              ],
            });
          } else if (graphic.symbol.type === 'simple-marker') {
            drawingsJson.push({
              drawingType: 'Point',
              featureName: 'user drawing',
              featureDescription: 'user drawing',
              color: graphic.symbol.color.toHex(),
              size: (graphic.symbol as SimpleMarkerSymbol).size,
              marker: 'marker',
              xyzarray: [
                {
                  x: point.x,
                  y: point.y,
                  z: 0,
                },
              ],
            });
          }
          break;
        case 'polyline':
          const polyline = geometry as Polyline;

          if (graphic.symbol.type === 'simple-line') {
            drawingsJson.push({
              drawingType: 'Polyline',
              featureName: 'user polyline',
              featureDescription: 'user polyline',
              color: graphic.symbol.color.toHex(),
              width: (graphic.symbol as SimpleLineSymbol).width,
              xyzarray: polyline.paths[0].map((p) => {
                return {
                  x: p[0],
                  y: p[1],
                  z: 0,
                };
              }),
            });
          }
          break;

        case 'polygon':
          const polygon = geometry as Polygon;
          if (graphic.symbol.type === 'simple-fill') {
            drawingsJson.push({
              drawingType: 'Circle',
              featureName: 'user polygon',
              featureDescription: 'user polygon',
              color: graphic.symbol.color.toHex(),
              width: (graphic.symbol as SimpleFillSymbol).outline.width,
              r: 0,
              xyzarray: polygon.rings[0].map((p) => {
                return {
                  x: p[0],
                  y: p[1],
                  z: 0,
                };
              }),
            });
          }

          break;

        default:
          break;
      }
    }
    return drawingsJson;
  }

  protected drawDesignElements(drawings): void {
    drawings.forEach((drawing) => {
      if (drawing.drawingType === 'Point') {
        //load Point drawing
        const point = new Point();
        point.x = drawing.xyzarray[0].x;
        point.y = drawing.xyzarray[0].y;
        point.z = drawing.xyzarray[0].z;
        point.spatialReference = projectsLibraryStore.projectSpatialReference!;

        const symbol = new SimpleMarkerSymbol();
        symbol.size = drawing.size;
        symbol.color = drawing.color;

        const graphic = new Graphic();
        graphic.geometry = point;
        graphic.symbol = symbol;

        this.drawings.loadDrawing(graphic, drawing.featureName);
      } else if (drawing.drawingType === 'Polyline') {
        //load Polyline drawing

        this.drawings.loadDrawing(
          this.generatePolyline(drawing.xyzarray, drawing.color, drawing.width),
          drawing.featureName
        );
      } else if (drawing.drawingType === 'Circle') {
        //load Polygon/Rectangle/Circle drawing

        this.drawings.loadDrawing(
          this.generatePolygon(drawing.xyzarray, drawing.color, drawing.width),
          drawing.featureName
        );
      } else if (drawing.drawingType === 'Text') {
        //load Text drawing

        const point = new Point();
        point.x = drawing.xyzarray[0].x;
        point.y = drawing.xyzarray[0].y;
        point.z = drawing.xyzarray[0].z;
        point.spatialReference = projectsLibraryStore.projectSpatialReference!;

        const graphic = new Graphic();
        graphic.geometry = point;

        const symbol = new TextSymbol();
        symbol.text = drawing.featureDescription;
        symbol.font.size = drawing.fontSize;
        symbol.horizontalAlignment = "left";
        symbol.color = drawing.color;
        symbol.font.style = drawing.fontStyle;
        symbol.font.family = drawing.fontName;
        graphic.symbol = symbol;

        this.drawings.loadDrawing(graphic, drawing.featureName);
      }
    });
  }
}
